import {
  FormControl,
  Select,
  MenuItem,
  SelectChangeEvent,
  Box,
  Typography,
  FormHelperText,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import React from 'react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';

interface IDropdownProps {
  options: Date[];
  onChange: (_event: SelectChangeEvent<unknown>) => void;
  value: Date | null;
  name?: string;
  onBlur?: (_event: React.FocusEvent<HTMLInputElement>) => void;
  error?: string;
  isTransalationProvided?: boolean;
  label: string;
}

const StyledSelect = styled(Select)(({ theme, error }) => ({
  borderRadius: '10px',
  color: 'black !important',
  background: '#FFF',
  '& .MuiOutlinedInput-root': {
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      border: 'none',
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      border: 'none',
    },
    '& .MuiOutlinedInput-notchedOutline': {
      border: 'none',
    },
    '&&&:before': {
      borderBottom: 'none',
    },
    '&&:after': {
      borderBottom: 'none',
    },
  },
  '& .MuiSelect-select': {
    borderRadius: '5px',
    display: 'flex',
    alignItems: 'center',
    padding: '16px',
    '&:focus': {
      borderRadius: '5px',
    },
  },
  '& fieldset': {
    padding: '16px',
    color: theme.palette.primary.main,
    border: error
      ? '1px solid var(--Alert-Sunset-Orange, #FF9A9A) !important'
      : '1px solid var(--Accent-Moonstone-Blue, #7CAEBE) !important',
  },
  '&:hover fieldset': {
    border: error
      ? '1px solid var(--Alert-Sunset-Orange, #FF9A9A) !important'
      : '1px solid var(--Accent-Moonstone-Blue, #7CAEBE) !important',
  },
  '&.Mui-focused fieldset': {
    border: '1px solid var(--Accent-Moonstone-Blue, #7CAEBE) !important',
  },
}));

function StyledDropdown({
  options,
  onChange,
  value,
  name,
  onBlur,
  error,
  isTransalationProvided,
  label,
}: IDropdownProps) {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  dayjs.locale(language);

  return (
    <Box display="flex" flexDirection="column" justifyContent="center" width="100%">
      <Typography variant="meta" color="primary" fontWeight={400}>
        {label}
      </Typography>
      <FormControl fullWidth>
        <StyledSelect
          labelId="dropdown-label"
          id="simple-select-dropdown"
          value={dayjs(value).unix()}
          name={name}
          onChange={onChange}
          inputProps={{ 'aria-label': 'Without label' }}
          displayEmpty
          onBlur={onBlur}
          error={!!error}
          fullWidth
          sx={{ color: '#000' }}
        >
          <MenuItem value="" disabled>
            <Typography variant="bodyRegular" style={{ color: '#444' }}>
              {options.length === 0
                ? t('bookings.bookingSettings.noAvailableTimeSlots')
                : t('activity.assignActivity.pleaseSelect')}
            </Typography>
          </MenuItem>
          {options.map((option) => (
            <MenuItem key={option.toString()} value={dayjs(option).unix()}>
              {isTransalationProvided
                ? t(dayjs(option).format('hh:mm A'))
                : dayjs(option).format('hh:mm A')}
            </MenuItem>
          ))}
        </StyledSelect>
      </FormControl>
      {error && (
        <FormHelperText sx={{ color: 'red', marginTop: '-2px' }}>{t(error)}</FormHelperText>
      )}
    </Box>
  );
}

export default StyledDropdown;
